import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import axios from '../config/api';
import { emitLiveRefresh } from '../utils/liveRefresh';

// Type definitions for push notifications
interface PushNotificationToken {
  value: string;
}

interface PushNotificationError {
  error: string;
}

interface PushNotification {
  id: string;
  title?: string;
  body?: string;
  data?: any;
}

interface PushNotificationAction {
  actionId: string;
  notification: {
    id: string;
    title?: string;
    body?: string;
    data?: any;
  };
}

class PushNotificationService {
  private isInitialized = false;
  private registrationListener: any = null;
  private pushNotificationReceivedListener: any = null;
  private pushNotificationActionPerformedListener: any = null;

  /**
   * Initialize push notifications for the app
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      console.log('Push notifications already initialized');
      return;
    }

    // Only initialize on native platforms
    if (!Capacitor.isNativePlatform()) {
      console.log('Push notifications only work on native platforms');
      return;
    }

    try {
      // Request permission to use push notifications
      const permissionResult = await PushNotifications.requestPermissions();
      
      if (permissionResult.receive === 'granted') {
        console.log('Push notification permission granted');
        
        // Register with Apple / Google to receive push notifications
        await PushNotifications.register();
        
        this.setupListeners();
        this.isInitialized = true;
      } else {
        console.warn('Push notification permission denied');
      }
    } catch (error) {
      console.error('Error initializing push notifications:', error);
    }
  }

  /**
   * Set up event listeners for push notifications
   */
  private setupListeners(): void {
    // On successful registration, we will receive the token
    this.registrationListener = PushNotifications.addListener('registration', async (token: PushNotificationToken) => {
      console.log('Push registration success, token: ' + token.value);
      
      // Send the token to our backend
      await this.registerToken(token.value);
    });

    // Handle registration errors
    PushNotifications.addListener('registrationError', (error: PushNotificationError) => {
      console.error('Error on registration: ' + JSON.stringify(error));
    });

    // Handle push notifications received while app is in foreground
    this.pushNotificationReceivedListener = PushNotifications.addListener(
      'pushNotificationReceived',
      async (notification: PushNotification) => {
        console.log('Push notification received:', notification);
        emitLiveRefresh();
      }
    );

    // Handle push notification actions (when user taps on notification)
    this.pushNotificationActionPerformedListener = PushNotifications.addListener(
      'pushNotificationActionPerformed',
      async (notification: PushNotificationAction) => {
        console.log('Push notification action performed:', notification);
        
        // Handle navigation based on notification data
        const data = notification.notification.data;
        emitLiveRefresh();
        if (data?.route) {
          // Navigate to specific route if provided
          window.location.href = data.route;
        }
      }
    );
  }

  /**
   * Register FCM token with backend
   */
  private async registerToken(token: string): Promise<void> {
    try {
      const platform = Capacitor.getPlatform(); // 'ios' or 'android'
      
      await axios.post('/api/push/register-token', {
        token,
        platform
      });
      
      console.log('FCM token registered successfully');
    } catch (error) {
      console.error('Error registering FCM token:', error);
    }
  }

  /**
   * Unregister FCM token (e.g., on logout)
   */
  async unregisterToken(token: string): Promise<void> {
    try {
      await axios.post('/api/push/unregister-token', {
        token
      });
      
      console.log('FCM token unregistered successfully');
    } catch (error) {
      console.error('Error unregistering FCM token:', error);
    }
  }

  /**
   * Get all registered tokens for the current user
   */
  async getRegisteredTokens(): Promise<string[]> {
    try {
      const response = await axios.get('/api/push/tokens');
      return response.data.tokens || [];
    } catch (error) {
      console.error('Error getting registered tokens:', error);
      return [];
    }
  }

  /**
   * Clean up listeners
   */
  cleanup(): void {
    if (this.registrationListener) {
      this.registrationListener.remove();
      this.registrationListener = null;
    }
    
    if (this.pushNotificationReceivedListener) {
      this.pushNotificationReceivedListener.remove();
      this.pushNotificationReceivedListener = null;
    }
    
    if (this.pushNotificationActionPerformedListener) {
      this.pushNotificationActionPerformedListener.remove();
      this.pushNotificationActionPerformedListener = null;
    }
    
    this.isInitialized = false;
  }
}

// Export singleton instance
export const pushNotificationService = new PushNotificationService();
